import React from "react";
import Head from "next/head";
import Link from "next/link";
import Navigation from "@/layout/navigation";
import WhoWeServeFeatureSection from "@/components/who-we-serve/WhoWeServeFeatureSection";

export default function ForHealthCoachesPage() {
  return (
    <>
      <Head>
        <title>
          Featcal for Health Coaches | Higher adherence, less chasing
        </title>
        <meta
          name="description"
          content="Featcal helps health coaches keep clients consistent between check-ins with structured tracking, in-app plans, and faster review-so you can coach outcomes, not reminders."
        />
      </Head>

      <header className="bg-gradient-to-b from-white from-20% to-blue-300 flex flex-col gap-16 items-center justify-between overflow-hidden px-8 xl:px-0 z-[51] relative pb-20">
        <Navigation />
        <div className="flex flex-col md:items-center md:justify-center gap-6 mt-2 z-10 md:text-center">
          <p className="text-sm font-semibold text-blue-700 bg-white/70 border border-blue-200 px-4 py-2 rounded-full w-fit md:mx-auto">
            For health coaching & behavior change
          </p>
          <h1 className="text-4xl md:text-5xl font-medium leading-tight max-w-3xl">
            Keep clients consistent between check-ins-without extra messaging.
          </h1>
          <p className="text-md max-w-2xl mx-auto text-slate-700">
            A simple client app for daily accountability + a coach dashboard
            that saves you time. Better adherence, better results, stronger
            retention.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:justify-center">
            <Link
              href="/start"
              className="bg-blue-600 text-white py-3 px-8 rounded-full font-semibold hover:bg-blue-700 transition-all duration-200 text-center"
            >
              Start for free
            </Link>
            <Link
              href="/#faq"
              className="bg-white text-slate-900 py-3 px-8 rounded-full font-semibold border border-blue-200 hover:bg-blue-50 transition-all duration-200 text-center"
            >
              See FAQs
            </Link>
          </div>
        </div>
      </header>

      <main id="main-content" className="bg-white">
        <section className="py-16 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto bg-white border border-slate-200 rounded-3xl p-8">
            <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-3">
              The real challenge: consistency without constant messaging
            </h2>
            <ul className="mt-4 space-y-3 text-slate-700">
              {[
                "Clients forget what to do between check-ins.",
                "Updates live in DMs, screenshots, and notes.",
                "You end up chasing adherence instead of coaching behavior change.",
              ].map((p) => (
                <li key={p} className="flex gap-3">
                  <span className="text-slate-400 font-bold">•</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="py-20 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-8">
            {[
              {
                title: "Less chasing, more coaching",
                desc: "Stop asking “Did you track?” Featcal keeps daily accountability inside the app.",
              },
              {
                title: "Track what drives outcomes",
                desc: "Weight, steps, sleep, hydration, meals, workouts-pick what matters for each client and review fast.",
              },
              {
                title: "Plans that actually get followed",
                desc: "Deliver routines, nutrition plans, and resources in-app, with reminders that reduce drop-offs.",
              },
            ].map((card) => (
              <div
                key={card.title}
                className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-3xl p-8 shadow-xl shadow-blue-100/30"
              >
                <h2 className="text-xl font-semibold text-slate-900 mb-3">
                  {card.title}
                </h2>
                <p className="text-slate-700 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="py-20 px-8 xl:px-0 pt-0">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-start">
            <div className="bg-white border border-slate-200 rounded-3xl p-8">
              <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-4">
                Great fit for
              </h2>
              <ul className="space-y-3 text-slate-700">
                {[
                  "Weight loss / weight gain coaching with weekly check-ins.",
                  "Habit coaching (steps, sleep, hydration, meal structure).",
                  "Metabolic health programs where consistency matters.",
                  "Group programs that need standardization.",
                ].map((p) => (
                  <li key={p} className="flex gap-3">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-3xl p-8 shadow-xl shadow-blue-100/30">
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">
                What Featcal replaces
              </h3>
              <ul className="space-y-3 text-slate-700">
                {[
                  "Constant DMs and “did you track?” messages.",
                  "Scattered screenshots and notes.",
                  "Spreadsheets for check-ins and progress.",
                  "Chasing adherence between sessions.",
                ].map((p) => (
                  <li key={p} className="flex gap-3">
                    <span className="text-slate-400 font-bold">•</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <WhoWeServeFeatureSection
          eyebrow="Daily check-ins"
          title="Accountability that runs itself"
          description="Clients track inside one simple app-so you stop chasing updates and your check-ins stay focused on behavior change."
          bullets={[
            "Fewer “did you track?” messages.",
            "More consistency between check-ins.",
            "A stronger, more scalable coaching system.",
          ]}
          imageSrc="/images/my-day.webp"
          imageAlt="Daily check-in view in the client app"
          align="right"
          imageFit="contain"
        />

        <section className="bg-gradient-to-b from-white to-blue-50 py-20 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto bg-white border border-blue-100 rounded-3xl p-8 md:p-10 shadow-xl shadow-blue-100/30">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h2 className="text-2xl md:text-3xl font-semibold text-slate-900">
                  Ready to coach outcomes, not reminders?
                </h2>
                <p className="text-slate-700 mt-2">
                  Start free and run your next check-in with less chasing.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  href="/start"
                  className="bg-blue-600 text-white py-3 px-8 rounded-full font-semibold hover:bg-blue-700 transition-all duration-200 text-center"
                >
                  Start for free
                </Link>
                <Link
                  href="/#faq"
                  className="bg-white text-slate-900 py-3 px-8 rounded-full font-semibold border border-blue-200 hover:bg-blue-50 transition-all duration-200 text-center"
                >
                  See FAQs
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
